import React, { useEffect, useRef, useState } from "react";
import { BigFilesAPI, ChunkedUploadOptions } from "../services/bigFiles";
import { RecordType, VideoType } from "../types";
import { GoProMetaDataReader, GoProMetadata } from "../utils/goProMetaDataReader";
import { campaignFileAccept, getVideoType, isVideoTypeCompliantWithRecord, slugifyFileName, validateCampaignFiles } from "../utils/videoAndRecord";
import { useCampaign } from '../contexts/useCampaign';

interface UploadModalProps {
  show: boolean;
  recordId?: number;
  recordType?: RecordType;
  onHide: () => void;
  onComplete: (result?: { success: boolean; message: string }) => void;
}

interface UploadItem {
  file: File;
  hashId?: string;
  status: "pending" | "uploading" | "completed" | "error";
  progress: number;
  error?: string;
  type: VideoType;
  originalName: string;
  suggestedName: string;
  cameraId: string;
  cameraModel?: string;
  cameraMode?: string;
  chunks?: {
    index: number;
    progress: number;
    status: "pending" | "uploading" | "completed" | "error";
  }[];
}

const UploadModal: React.FC<UploadModalProps> = ({
  show,
  onHide,
  onComplete,
  recordId,
  recordType,
}) => {
  const { selectedCampaign } = useCampaign();
  const campaignType = recordId ? selectedCampaign?.type : undefined;
  const [uploadQueue, setUploadQueue] = useState<UploadItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const chunkSizeInMo = 50;

  // Reset state when modal is shown
  useEffect(() => {
    if (show) {
      setError(null);
      setUploadQueue([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, [show]);


  // Validate files before adding them to the queue
  const validateFiles = (files: File[]): { valid: boolean; message?: string } => {
    if (files.length === 0) return { valid: true };
    if (recordId) {
      const message = validateCampaignFiles(files, campaignType);
      if (message) return { valid: false, message };
    }

    // Check if all files are of the same type and different from record type
    const fileTypes = files.map(file => getVideoType(file.name));
    const uniqueTypes = new Set(fileTypes);

    if (uniqueTypes.size > 1) {
      return {
        valid: false,
        message: "All files must be of the same type (either all PHOTO or all PANORAMA)"
      };
    }

    if (recordType && !isVideoTypeCompliantWithRecord(fileTypes[0], recordType)) {
      return {
        valid: false,
        message: "All new videos should be of the type of the record"
      };
    }


    return { valid: true };
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);

      // Validate files
      const validation = validateFiles(files);
      if (!validation.valid) {
        setError(validation.message || "Invalid files");
        // Clear the file input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }

      // Clear any previous errors
      setError(null);

      // Process files and generate suggested names based on creation date
      const processedFiles = files.map((file) => {


        // Calculate number of chunks if using chunked upload
        const chunkSize = 5 * 1024 * 1024; // 5MB chunks
        const numChunks = Math.ceil(file.size / chunkSize);

        // Get file creation/modification date
        const fileDate = new Date(file.lastModified);

        // Format date as YYYYMMDD_HHMMSS
        const formattedDate = fileDate.toISOString()
          .replace(/[-:]/g, '')  // Remove dashes and colons
          .replace('T', '_')     // Replace T with underscore
          .slice(0, 15);         // Take only YYYYMMDD_HHMMSS part

        // Get original filename without extension
        const fileNameParts = file.name.split('.');
        const extension = fileNameParts.pop() || '';
        const baseName = fileNameParts.join('.');

        // Create suggested name with timestamp prefix
        const suggestedName = slugifyFileName(`${formattedDate}_${baseName}.${extension}`);

        return {
          file,
          status: "pending" as "pending" | "uploading" | "completed" | "error",
          progress: 0,
          type: getVideoType(file.name),
          originalName: file.name,
          suggestedName: suggestedName,
          cameraId: "undefined",
          chunks: Array.from({ length: numChunks }, (_, index) => ({
            index,
            progress: 0,
            status: "pending" as const
          }))

        } as UploadItem;
      });
      let pendingCallbacks = processedFiles.length;
      const allCallbacksCompleted = () => {
        pendingCallbacks--;
        console.log("allCallbacksCompleted", pendingCallbacks)
        if (pendingCallbacks === 0) {
          console.log(processedFiles)
          // All callbacks completed, now update the state
          setUploadQueue(processedFiles);
          console.log("Added files:", processedFiles.length);
        }
      };

      processedFiles.forEach(processedFile => {
        const setCamIdCallback = (data: GoProMetadata | string) => {
          console.log("setCamId", data);
          if (typeof data === 'string') {
            processedFile.cameraId = data;
          } else if (typeof data === 'object') {
            processedFile.cameraId = data.cameraId;
            if (data.cameraModel) processedFile.cameraModel = data.cameraModel;
            if (data.cameraMode) processedFile.cameraMode = data.cameraMode;
          } else {
            processedFile.cameraId = "undefined";
          }
          allCallbacksCompleted();
        };
        new GoProMetaDataReader(processedFile.file, setCamIdCallback);
      });

    }
  };

  const updateItemProgress = (index: number, progress: number) => {
    setUploadQueue((queue) =>
      queue.map((item, i) => (i === index ? { ...item, progress } : item))
    );
  };

  const updateItemStatus = (
    index: number,
    status: UploadItem["status"],
    error?: string
  ) => {
    setUploadQueue((queue) =>
      queue.map((item, i) => (i === index ? { ...item, status, error } : item))
    );
  };

  const updateChunkProgress = (itemIndex: number, chunkIndex: number, progress: number) => {
    setUploadQueue((queue) => {
      const newQueue = [...queue];
      const item = { ...newQueue[itemIndex] };

      if (item.chunks) {
        const chunks = [...item.chunks];
        chunks[chunkIndex] = {
          ...chunks[chunkIndex],
          progress
        };
        item.chunks = chunks;

        // Calculate overall progress based on all chunks
        const totalProgress = chunks.reduce((sum, chunk) => sum + chunk.progress, 0) / chunks.length;
        item.progress = Math.round(totalProgress);
      }

      newQueue[itemIndex] = item;
      return newQueue;
    });
  };

  const updateChunkStatus = (
    itemIndex: number,
    chunkIndex: number,
    status: "pending" | "uploading" | "completed" | "error"
  ) => {
    setUploadQueue((queue) => {
      const newQueue = [...queue];
      const item = { ...newQueue[itemIndex] };

      if (item.chunks) {
        const chunks = [...item.chunks];
        chunks[chunkIndex] = {
          ...chunks[chunkIndex],
          status
        };
        item.chunks = chunks;
      }

      newQueue[itemIndex] = item;
      return newQueue;
    });
  };

  const uploadFile = async (item: UploadItem, index: number) => {
    try {
      updateItemStatus(index, "uploading");

      const finalFileName = item.suggestedName;
      const hashId = item.hashId || "unknown";

      const options: ChunkedUploadOptions = {
        onProgress: (progress) => {
          updateItemProgress(index, progress);
        },
        onChunkProgress: (chunkIndex, progress) => {
          updateChunkProgress(index, chunkIndex, progress);
        },
        onChunkComplete: (chunkIndex) => {
          updateChunkStatus(index, chunkIndex, "completed");
        },
        onChunkError: (chunkIndex, error) => {
          updateChunkStatus(index, chunkIndex, "error");
          console.error(`Chunk ${chunkIndex} upload error:`, error);
        }
      };


      console.log("uploadFile: Starting chunked upload with metadata:", {
        cameraId: item.cameraId,
        cameraModel: item.cameraModel,
        cameraMode: item.cameraMode
      });
      const result = await BigFilesAPI.uploadChunked(
        finalFileName, item.file, hashId,
        item.cameraId, options, recordId, item.cameraModel, item.cameraMode)

      if (result.success) {
        updateItemStatus(index, "completed");
        updateItemProgress(index, 100);

        // Notify parent component of successful upload
        onComplete({
          success: true,
          message: `Successfully uploaded ${finalFileName}`
        });

        return true;
      } else {
        throw new Error(result.error || "Upload failed");
      }

    } catch (error) {
      console.error("Upload error:", error);
      const errorMessage = error instanceof Error ? error.message : "Upload failed";

      updateItemStatus(index, "error", errorMessage);

      // Notify parent component of failed upload
      onComplete({
        success: false,
        message: `Failed to upload ${item.suggestedName || item.file.name}: ${errorMessage}`
      });

      throw error;
    }
  };

  const handleUpload = async () => {
    if (uploadQueue.length === 0) {
      alert("Please select files to upload");
      return;
    }

    const validation = validateFiles(uploadQueue.filter(item => item.status !== 'completed').map(item => item.file));
    if (!validation.valid) {
      setError(validation.message || 'Invalid files');
      return;
    }

    try {
      // Upload files in parallel
      const uploadPromises = uploadQueue.map((item, index) => {
        if (item.status === "completed") {
          return Promise.resolve(); // Skip already completed uploads
        }
        // Wrap each upload in a Promise that won't reject
        return uploadFile(item, index).catch((error) => {
          console.error(`Upload failed for ${item.file.name}:`, error);
          // Error is already handled in uploadFile function
          // Return undefined to continue with other uploads
        });
      });

      await Promise.all(uploadPromises);

      // Check if all files are either completed or errored
      const allFinished = uploadQueue.every(
        (item) => item.status === "completed" || item.status === "error"
      );

      if (allFinished) {
        // Check if at least one file was uploaded successfully
        const hasSuccessful = uploadQueue.some(
          (item) => item.status === "completed"
        );

        // Send a final summary to the parent component
        if (hasSuccessful) {
          const successCount = uploadQueue.filter(item => item.status === "completed").length;
          const errorCount = uploadQueue.filter(item => item.status === "error").length;

          onComplete({
            success: true,
            message: `Upload complete: ${successCount} file(s) uploaded successfully${errorCount > 0 ? `, ${errorCount} failed` : ''}`
          });
        } else {
          onComplete({
            success: false,
            message: "No files were uploaded successfully"
          });
        }
      }
    } catch (error) {
      console.error("Upload error:", error);
    }
  };

  if (!show) return null;

  return (
    <div
      className="modal show d-block"
      style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
    >
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              Upload Videos
            </h5>
            <button
              type="button"
              className="btn-close"
              onClick={onHide}
            ></button>
          </div>
          <div className="modal-body">
            {error && (
              <div className="alert alert-danger mb-3">
                {error}
              </div>
            )}

            <div className="mb-3">
              <input
                type="file"
                className="form-control"
                accept={recordId ? campaignFileAccept(recordType === 'UNKNOWN' ? campaignType : recordType) : 'video/*,.360'}
                multiple
                ref={fileInputRef}
                onChange={handleFileSelect}
              />
              <small className="text-muted d-block mt-1">
                Large files (&gt;{chunkSizeInMo}Mo) will be automatically uploaded in chunks for better reliability
              </small>
              <small className="text-muted d-block mt-1">
                {recordId ? 'MP4 files are PHOTO; .360 files are PANORAMA. Files must match the record and campaign type.' : 'Files with .360 extension are PANORAMA; other videos are PHOTO.'}
              </small>
            </div>

            <div className="upload-queue">
              {uploadQueue.map((item, index) => (
                <div key={index} className="upload-item mb-3">
                  <div className="d-flex justify-content-between mb-2">
                    <div>
                      <div>
                        <strong>Original:</strong> {item.originalName}
                        <span className={`badge ms-2 ${item.type === 'PANORAMA' ? 'bg-success' : 'bg-primary'}`}>
                          {item.type}
                        </span>
                      </div>
                      <div className="mt-1">
                        <strong>Suggested:</strong> {item.suggestedName}
                      </div>
                    </div>
                    <div className="upload-status">
                      {item.status === "pending"
                        ? "Pending"
                        : item.status === "uploading"
                          ? "Uploading..."
                          : item.status === "completed"
                            ? "Completed"
                            : `Error: ${item.error}`}
                    </div>
                  </div>
                  <div className="progress mb-1">
                    <div
                      className={`progress-bar ${item.status === "error" ? "bg-danger" : ""
                        }`}
                      role="progressbar"
                      style={{ width: `${item.progress}%` }}
                    >
                      {item.progress}%
                    </div>
                  </div>

                  {/* Show individual chunk progress for chunked uploads */}
                  {item.chunks && item.status === "uploading" && (
                    <div className="chunks-progress mt-2">
                      <small className="d-block mb-1">Chunks progress:</small>
                      <div className="d-flex flex-wrap gap-1">
                        {item.chunks.map((chunk) => (
                          <div
                            key={chunk.index}
                            className="chunk-indicator"
                            style={{
                              width: '20px',
                              height: '10px',
                              backgroundColor:
                                chunk.status === 'completed' ? '#28a745' :
                                  chunk.status === 'error' ? '#dc3545' :
                                    chunk.status === 'uploading' ? '#007bff' : '#e9ecef',
                              borderRadius: '2px'
                            }}
                            title={`Chunk ${chunk.index + 1}: ${chunk.progress}%`}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onHide}
            >
              Close
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleUpload}
              disabled={
                !!error ||
                uploadQueue.length === 0 ||
                uploadQueue.every((item) => item.status === "completed")
              }
            >
              Upload All
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UploadModal;
